import { useCallback, useRef, useState } from 'react';

import { normalizeDegrees, UnitConversions } from '../../../general-utilities/numbers-util';
import { LiveLineData, MapType, MarkerData } from '../../Mapbox/mapTypes';
import { createHomeMarkerImage } from '../HomeMarker';
import { TelemetryData } from '../types';

interface UseCrewTelemetryProps {
  liveLine: LiveLineData;
  setLiveLine: React.Dispatch<React.SetStateAction<LiveLineData>>;
  mapRef: React.MutableRefObject<MapType | null>;
  markerImages: { [id: string]: MarkerData };
  setMarkerImages: React.Dispatch<React.SetStateAction<{ [id: string]: MarkerData }>>;
  onAglUpdate?: (agl: string) => void;
}

const HOME_POSITION_MARKER_SIZE = 17;

export const useCrewTelemetry = ({
  liveLine,
  setLiveLine,
  mapRef,
  markerImages,
  setMarkerImages,
  onAglUpdate
}: UseCrewTelemetryProps) => {
  const [latestCrewTelemetry, setLatestCrewTelemetry] = useState<TelemetryData | null>(null);
  const [agl, setAgl] = useState<string | null>(null);
  const lastCrewTelemetryTimestampRef = useRef<number>(0);
  const latestCrewTelemetryRef = useRef<TelemetryData | null>(null);

  const handleTelemetryMessage = useCallback(
    async (data: unknown) => {
      const telemetryData = data as TelemetryData;
      const timestamp = telemetryData.timestamp?.utc_time_us_rpi
        ? new Date(telemetryData.timestamp.utc_time_us_rpi).getTime()
        : telemetryData.timestamp?.utc_time_us_pixhawk
        ? new Date(telemetryData.timestamp.utc_time_us_pixhawk).getTime()
        : Date.now();

      if (!lastCrewTelemetryTimestampRef.current || timestamp > lastCrewTelemetryTimestampRef.current) {
        lastCrewTelemetryTimestampRef.current = timestamp;
        latestCrewTelemetryRef.current = telemetryData;

        const { position } = telemetryData;
        if (position?.longitude_deg !== undefined && position?.latitude_deg !== undefined) {
          const markerAngle = normalizeDegrees(telemetryData.attitude_euler?.yaw_deg ?? 0);
          const gimbalAngle = normalizeDegrees(markerAngle + normalizeDegrees(telemetryData.gimbal_info?.yaw_deg ?? 0));
          const newPoint: [number, number] = [position.longitude_deg, position.latitude_deg];

          // Update liveLine immutably, avoiding duplicates
          setLiveLine((prev) => {
            // Check if the last point is the same as the new point to avoid duplicates
            const lastPoint = prev.lines[prev.lines.length - 1];
            if (lastPoint && lastPoint[0] === newPoint[0] && lastPoint[1] === newPoint[1]) {
              // Same point, just update angles
              return {
                ...prev,
                markerAngle,
                gimbalAngle
              };
            }

            // New point, add it
            return {
              ...prev,
              lines: [...prev.lines, newPoint],
              markerAngle,
              gimbalAngle
            };
          });

          if (mapRef.current) {
            const elevation = await mapRef.current.getElevation([position.longitude_deg, position.latitude_deg]);
            const previousAgl = agl;
            let aglValue: string;
            if (elevation === '--') {
              aglValue = '--';
            } else {
              aglValue = UnitConversions.mToF((position.absolute_altitude_m ?? 0) - elevation).toFixed(1);
            }
            // Update AGL state
            setAgl(aglValue);
            // Notify parent component of AGL update if it changed
            if (onAglUpdate && aglValue !== previousAgl) {
              onAglUpdate(aglValue);
            }
          }
        }

        // Update home position marker when home position data is available
        const { home_position } = telemetryData;

        if (home_position?.latitude_deg !== undefined && home_position?.longitude_deg !== undefined) {
          const homeLat = home_position.latitude_deg;
          const homeLng = home_position.longitude_deg;

          // Only update if position changed or marker doesn't exist
          const existingMarker = markerImages.home_position;
          if (!existingMarker || existingMarker.position[0] !== homeLng || existingMarker.position[1] !== homeLat) {
            const homeMarkerImage = createHomeMarkerImage(HOME_POSITION_MARKER_SIZE);
            setMarkerImages((prev) => ({
              ...prev,
              home_position: {
                position: [homeLng, homeLat],
                image: homeMarkerImage,
                id: 'home_position'
              }
            }));
          }
        }

        setLatestCrewTelemetry(telemetryData);
      }
    },
    [setLiveLine, mapRef, agl, markerImages, setMarkerImages, onAglUpdate]
  );

  const getLastTelemetryTimestamp = useCallback(() => {
    return lastCrewTelemetryTimestampRef.current;
  }, []);

  return {
    latestCrewTelemetry,
    handleTelemetryMessage,
    getLastTelemetryTimestamp,
    agl
  };
};
